<div class="widget"
     [style]="rootStyle"
     [attr.dir]="dir"
     [class.bubbles-outlined]="bubbleOutlined"
     [class.bubbles-plain]="bubblePlain"
     [class.with-avatars]="showAvatar"
     [class.embedded]="embedded">

    <header class="header">
        <span class="avatar-wrap" aria-hidden="true">
            <span class="avatar avatar-md">
                <img *ngIf="customization?.iconUrl" [src]="customization?.iconUrl" alt="" />
                <span *ngIf="!customization?.iconUrl">{{ avatarInitial }}</span>
            </span>
            <span class="status-dot"></span>
        </span>
        <div class="header-titles">
            <h2 class="title">{{ customization?.title }}</h2>
        </div>
        <button *ngIf="messages.length" type="button" class="icon-btn on-brand"
                (click)="startNewChat()" [attr.aria-label]="strings.newChat" [title]="strings.newChat">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4z"/><path d="M13.5 6.5l4 4"/></svg>
        </button>
        <button *ngIf="hostCanClose" type="button" class="icon-btn on-brand"
                (click)="closeWidget()" [attr.aria-label]="strings.close" [title]="strings.close">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>
        </button>
    </header>

    <div class="log-wrap">
        <div #scrollContainer (scroll)="onScroll()" (click)="onContentClick($event)"
             class="log" role="log" aria-live="polite" aria-relevant="additions text"
             [attr.aria-label]="strings.conversation">

            <!-- Welcome: the tenant's greeting while the conversation is empty, under the agent's
                 avatar — led by its first line when it has several, else by the agent's name. It
                 gives way to the first message and comes back with New chat.
                 Display-only, so it is not in `messages`: it is never sent to the chat API.
                 Tenant-controlled text, so plain interpolation — never markdown/HTML; pre-line
                 keeps its line breaks. -->
            <section *ngIf="welcomeMessage && !messages.length" class="welcome-message">
                <span class="avatar-wrap" aria-hidden="true">
                    <span class="avatar avatar-lg">
                        <img *ngIf="customization?.iconUrl" [src]="customization?.iconUrl" alt="" />
                        <span *ngIf="!customization?.iconUrl">{{ avatarInitial }}</span>
                    </span>
                    <span class="status-dot"></span>
                </span>
                <h3 *ngIf="welcomeTitle" class="welcome-name" dir="auto">{{ welcomeTitle }}</h3>
                <p class="welcome-message-text" dir="auto">{{ welcomeBody }}</p>
            </section>

            <div *ngFor="let msg of messages; trackBy: trackByMessage; let lastMsg = last"
                 class="row"
                 [class.row-user]="msg.from === msgType.User"
                 [class.row-agent]="msg.from === msgType.Agent">

                <!-- Visitor: one plain-text bubble in the brand colour. -->
                <ng-container *ngIf="msg.from === msgType.User">
                    <div class="bubble bubble-user" dir="auto">{{ msg.parts[0].text }}</div>
                    <time *ngIf="showTimestamps" class="time">{{ msg.time }}</time>
                </ng-container>

                <ng-container *ngIf="msg.from === msgType.Agent">
                    <span *ngIf="msg.showName && resolvedAgentName" class="agent-name">{{ resolvedAgentName }}</span>
                    <div class="agent-line">
                        <!-- The avatar slot stays on every bubble so a run of replies lines up; only the
                             first of the run shows the picture. -->
                        <img *ngIf="showAvatar" class="avatar avatar-sm" [class.avatar-ghost]="!msg.showName"
                             [src]="customization?.iconUrl" alt="" />
                        <div class="agent-body">
                            <!-- Ordered parts: text segments and tool indicators are rendered in the exact
                                 order they arrived, so each tool call appears inline in its context. -->
                            <ng-container *ngFor="let part of msg.parts; trackBy: trackByPart; let lastPart = last">
                                <!-- Tool-call status. displayText is tenant-controlled, so it is rendered as
                                     plain text via interpolation — never as markdown/HTML. -->
                                <div *ngIf="part.kind === 'tool'"
                                     class="tool-indicator"
                                     [class.running]="part.status === 'running'"
                                     [class.done]="part.status !== 'running'"
                                     role="status"
                                     [attr.aria-label]="part.displayText">
                                    <span class="tool-spinner" *ngIf="part.status === 'running'"></span>
                                    <svg class="tool-icon ok" *ngIf="part.status === 'ok'" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.7 2.7L16 9.8"/></svg>
                                    <span class="tool-icon err" *ngIf="part.status === 'error'" aria-hidden="true">!</span>
                                    <span class="tool-label">{{ part.displayText }}</span>
                                </div>

                                <div *ngIf="part.kind === 'text'"
                                     class="bubble bubble-agent markdown-content"
                                     [class.caret]="msg.streaming && lastPart"
                                     dir="auto"
                                     markdown [data]="part.text || ''"></div>

                                <div *ngIf="part.kind === 'approval'"
                                     class="approval-card"
                                     [class.decided]="part.approvalState !== 'pending'"
                                     role="group"
                                     [attr.aria-label]="strings.confirm + ': ' + part.approvalDisplayName">
                                    <div class="approval-title">
                                        <span class="approval-icon" aria-hidden="true">
                                            <svg viewBox="0 0 24 24"><path d="M12 4l9 16H3L12 4z"/><path d="M12 10v4.5"/><path d="M12 17.5v.01"/></svg>
                                        </span>
                                        <span>{{ strings.confirm }}: {{ part.approvalDisplayName }}</span>
                                    </div>
                                    <dl class="approval-args" *ngIf="part.approvalArgs?.length">
                                        <ng-container *ngFor="let arg of part.approvalArgs">
                                            <dt>{{ arg.name }}</dt>
                                            <dd>{{ arg.value }}</dd>
                                        </ng-container>
                                    </dl>
                                    <div class="approval-actions" *ngIf="part.approvalState === 'pending'">
                                        <button type="button"
                                                class="approval-btn approve"
                                                (click)="onApprovalDecision(part, true)">{{ strings.approve }}</button>
                                        <button type="button"
                                                class="approval-btn reject"
                                                (click)="onApprovalDecision(part, false)">{{ strings.reject }}</button>
                                    </div>
                                    <div *ngIf="part.approvalState === 'approved'" class="approval-status ok" role="status">{{ strings.approved }}</div>
                                    <div *ngIf="part.approvalState === 'rejected'" class="approval-status err" role="status">{{ strings.rejected }}</div>
                                    <div *ngIf="part.approvalState === 'expired'" class="approval-status err" role="status">{{ strings.expired }}</div>
                                    <div *ngIf="part.approvalState === 'failed'" class="approval-status err" role="status">{{ strings.failed }}</div>
                                </div>
                            </ng-container>

                            <div *ngIf="msg.typing" class="typing" aria-hidden="true"><span></span><span></span><span></span></div>

                            <div *ngIf="msg.budgetExhausted" class="tool-budget-notice">{{ strings.toolBudget }}</div>

                            <!-- Extras, once the answer is complete. Sources are web pages only — never an
                                 uploaded file — each with an http(s) address; titles come from the
                                 tenant's site, so plain interpolation. -->
                            <div *ngIf="msg.done && showSources && msg.sources?.length" class="sources">
                                <span class="sources-label">{{ strings.sources }}</span>
                                <a *ngFor="let source of msg.sources; trackBy: trackBySource"
                                   class="source" [href]="source.url" target="_blank" rel="noopener noreferrer">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></svg>
                                    {{ source.title }}
                                </a>
                            </div>

                            <div *ngIf="msg.done && (showFeedback || showTimestamps)" class="answer-actions">
                                <ng-container *ngIf="showFeedback">
                                    <button type="button" class="icon-btn small" (click)="copyAnswer(msg)"
                                            [attr.aria-label]="msg.copied ? strings.copied : strings.copy" [title]="msg.copied ? strings.copied : strings.copy">
                                        <svg *ngIf="!msg.copied" viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h8"/></svg>
                                        <svg *ngIf="msg.copied" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
                                    </button>
                                    <ng-container *ngIf="msg.messageId">
                                        <button type="button" class="icon-btn small rate" [class.chosen]="msg.feedback === 'up'"
                                                (click)="rateAnswer(msg, 'up')" [attr.aria-pressed]="msg.feedback === 'up'"
                                                [attr.aria-label]="strings.helpful" [title]="strings.helpful">
                                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11v9H4v-9h3z"/><path d="M7 11l4-7c1.1 0 2 .9 2 2v4h5.2a2 2 0 0 1 2 2.3l-1.1 6A2 2 0 0 1 17.1 20H7"/></svg>
                                        </button>
                                        <button type="button" class="icon-btn small rate down" [class.chosen]="msg.feedback === 'down'"
                                                (click)="rateAnswer(msg, 'down')" [attr.aria-pressed]="msg.feedback === 'down'"
                                                [attr.aria-label]="strings.notHelpful" [title]="strings.notHelpful">
                                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11v9H4v-9h3z"/><path d="M7 11l4-7c1.1 0 2 .9 2 2v4h5.2a2 2 0 0 1 2 2.3l-1.1 6A2 2 0 0 1 17.1 20H7"/></svg>
                                        </button>
                                    </ng-container>
                                </ng-container>
                                <time *ngIf="showTimestamps" class="time">{{ msg.time }}</time>
                            </div>

                            <!-- Follow-ups belong to the latest answer only; a new question retires them. -->
                            <div *ngIf="lastMsg && msg.done && followUpsEnabled && msg.followUps?.length && !hasPendingApproval"
                                 class="follow-ups" role="group" [attr.aria-label]="strings.suggestedQuestions">
                                <button *ngFor="let q of msg.followUps; trackBy: trackBySuggestion"
                                        type="button" class="follow-up" dir="auto" (click)="askSuggestedQuestion(q)">
                                    <svg class="follow-up-spark" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l1.9 5.6a2 2 0 0 0 1.3 1.3l5.6 1.9-5.6 1.9a2 2 0 0 0-1.3 1.3L12 20.5l-1.9-5.6a2 2 0 0 0-1.3-1.3L3.2 11.3l5.6-1.9a2 2 0 0 0 1.3-1.3z"/></svg>
                                    <span class="follow-up-label">{{ q }}</span>
                                    <svg class="follow-up-arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>

        <button *ngIf="showNewMessages" type="button" class="new-messages" (click)="jumpToLatest()">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14"/><path d="M6 13l6 6 6-6"/></svg>
            {{ strings.newMessage }}
        </button>
    </div>

    <div *ngIf="hasPendingApproval"
         class="pending-approval-notice"
         role="status" aria-live="polite">
        {{ strings.pendingApproval }}
    </div>

    <!-- Opening questions. Deliberately outside the scroll container so they sit against the
         composer rather than floating at the top of the empty conversation — and so the
         role="log" region above doesn't announce them as incoming messages. Rendered only
         while the conversation is empty; picking one sends it as the visitor's first message.
         Tenant-controlled text, so plain interpolation — never markdown/HTML. -->
    <div *ngIf="showSuggestions"
         class="suggested-questions"
         [class.as-cards]="suggestionsAsCards"
         role="group" [attr.aria-label]="strings.suggestedQuestions">
        <button *ngFor="let q of suggestedQuestions; trackBy: trackBySuggestion"
                type="button"
                class="suggestion-chip"
                dir="auto"
                (click)="askSuggestedQuestion(q)">
            <span *ngIf="suggestionsAsCards" class="suggestion-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4A8 8 0 1 1 20 12z"/></svg>
            </span>
            <span class="suggestion-label">{{ q }}</span>
            <svg *ngIf="suggestionsAsCards" class="suggestion-arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>
        </button>
    </div>

    <footer class="composer" (click)="onContentClick($event)">
        <div class="field" [class.disabled]="hasPendingApproval">
            <textarea #composerInput
                      rows="1"
                      class="input"
                      dir="auto"
                      [ngModel]="question"
                      (ngModelChange)="onQuestionChange($event)"
                      (input)="autoGrow()"
                      (keydown.enter)="onComposerEnter($event)"
                      [attr.aria-label]="placeholder"
                      [placeholder]="placeholder"
                      [disabled]="hasPendingApproval"></textarea>
            <button type="button"
                    class="send"
                    (click)="sendMessage()"
                    [attr.aria-label]="strings.send"
                    [title]="strings.send"
                    [disabled]="hasPendingApproval || !canSend">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5"/><path d="M5.5 11.5L12 5l6.5 6.5"/></svg>
            </button>
        </div>
        <!-- Tenant-controlled, so plain interpolation. -->
        <p *ngIf="disclaimer" class="disclaimer" dir="auto">{{ disclaimer }}</p>
        <!-- Shown only when the API says the tenant's plan carries the branding. The anchor goes
             through onContentClick, so it opens in a new tab without unloading this frame. -->
        <a *ngIf="showPoweredBy" class="powered-by" [href]="poweredByUrl" target="_blank" rel="noopener">
            {{ strings.poweredBy }} <strong>Nubo</strong>
        </a>
    </footer>
</div>
